import React from "react";
import { Box, Flex, Input, Spacer, useToast } from "@chakra-ui/react";
import { Spin, Table } from "antd";
import { get } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useRestaurantMaster } from "../../store/restaurant/reducer";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, MoreVertical } from "lucide-react";
import axios from "axios";

interface Props {
  query: string;
}

const SortableRow = ({ children, ...props }: any) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props["data-row-key"],
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 }),
    transition,
    ...(isDragging ? { position: "relative", zIndex: 999 } : {}),
  };

  return (
    <tr {...props} ref={setNodeRef} style={style}>
      {React.Children.map(children, (child) => {
        if ((child as React.ReactElement).key === "drag-handle") {
          return React.cloneElement(child as React.ReactElement, {
            children: (
              <div
                {...attributes}
                {...listeners}
                className="cursor-move flex items-center justify-center"
                style={{ width: 24, height: 24 }}
              >
                <GripVertical size={16} className="text-gray-400" />
              </div>
            ),
          });
        }
        return child;
      })}
    </tr>
  );
};

const RestaurantsequenceTable: React.FC<Props> = ({ query }) => {
  const { masterRestaurant, busy } = useRestaurantMaster();
  const searchColumns = useMemo(() => ["name"], []);
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [loading, setloading] = useState(true);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const search = (restaurant: Record<string, any>) => {
    return searchColumns.some((column) => {
      return (
        get(restaurant, column, "")
          .toString()
          .toLowerCase()
          .indexOf(searchQuery.toLowerCase()) > -1
      );
    });
  };

  useEffect(() => {
    if (masterRestaurant) {
      const transformedData = masterRestaurant.map(
        (restaurant: any, index: number) => ({
          key: restaurant._id,
          index: index + 1,
          order: restaurant.order,
          name: restaurant.name,
          address: `${restaurant.address.street}, ${restaurant.address.city}, ${restaurant.address.state}`,
          phoneNumber: restaurant.contact.phone,
          email: restaurant.contact.email,
          cuisineType: restaurant.cuisines.join(", "),
          image: restaurant.image?.url
            ? `${process.env.REACT_APP_IMAGE_BASE_URL}/${restaurant.image?.url}`
            : "",
          createdDate: new Date().toLocaleDateString(),
          originalData: restaurant,
        })
      );

      const filtered = searchQuery
        ? transformedData.filter(search)
        : transformedData.sort((a: any, b: any) => a.order - b.order);

      setFilteredData(filtered);
    }
    setloading(false);
  }, [searchQuery, masterRestaurant]);

  const handleSearch = (value: string) => {
    setSearchQuery(value);
  };

  const sendUpdatedOrder = async (reorderedArray: any[]) => {
    const payload = {
      sortingOrder: reorderedArray.map((item) => ({
        key: item.key,
        order: item.order,
      })),
    };

    console.log("Sending payload:", payload);

    try {
      const response = await axios({
        method: "patch",
        url: `${process.env.REACT_APP_BASE_URL}/admin/restaurant/update-order`,
        data: payload,
        withCredentials: true,
      });
      console.log("Update order response:", response);
      toast({
        title: "Success",
        description: "Order updated successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      console.error("Error updating order:", error);
    }
  };

  const handleDragEnd = ({ active, over }: any) => {
    if (active.id !== over?.id) {
      setFilteredData((previous) => {
        const activeIndex = previous.findIndex((i) => i.name === active.id);
        const overIndex = previous.findIndex((i) => i.name === over.id);

        const newArray = arrayMove(previous, activeIndex, overIndex);
        const reorderedArray = newArray.map((item, index) => ({
          ...item,
          order: index + 1,
        }));
        console.log("Updated table data after drag:", reorderedArray);
        sendUpdatedOrder(reorderedArray);
        return reorderedArray;
      });
    }
  };

  const SingleImage = ({ image }: { image: string }) => (
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        overflow: "hidden",
        backgroundColor: "white",
      }}
    >
      <img
        src={image}
        alt="Banner"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
    </div>
  );

  const columns = [
    {
      width: 50,
      key: "drag-handle",
      render: () => null,
      className: "drag-visible",
    },
    {
      title: "Restaurant Sequence",
      dataIndex: "index",
      width: 120,
      render: (_: any, __: any, index: number) => `${index + 1}`,
    },
    {
      title: "Image",
      dataIndex: "image",
      width: 100,
      render: (image: string) => <SingleImage image={image} />,
    },
    {
      title: "Restaurant Name",
      dataIndex: "name",
      width: 200,
    },
    {
      title: "Address",
      dataIndex: "address",
      width: 230,
    },
    {
      title: "Phone Number",
      dataIndex: "phoneNumber",
      width: 150,
    },
    {
      title: "Email",
      dataIndex: "email",
      width: 200,
    },
    {
      title: "Cuisine Type",
      dataIndex: "cuisineType",
      width: 120,
    },
    {
      title: "Created Date",
      dataIndex: "createdDate",
      width: 100,
    },
  ];

  return (
    <div
      style={{
        width: "96vw",
        maxWidth: "96vw",
        maxHeight: "86vh",
        height: "86vh",
        top: "8vh ",
        left: "4vw",
        overflowY: "auto",
        position: "relative",
        backgroundColor: "#EEEEEE",
        padding: "30px",
      }}
    >
      <style>
        {`
          .drag-visible {
            width: 48px !important;
            min-width: 48px !important;
            padding: 16px 8px !important;
          }
          .ant-table-cell {
            vertical-align: middle !important;
          }
        `}
      </style>
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <Flex w="full" alignItems="center" justifyContent="space-between">
          <Box
            display="flex"
            alignItems="center"
            bg="#FFF"
            border="1px solid #b2b2b233"
            borderRadius="12px"
            w="498px"
            padding="8px"
          >
            <Box
              pl={2}
              display="flex"
              alignItems="center"
              color="#9E9E9E"
              fontSize="2xl"
            >
              <img
                src="/icons/searchlogo.svg"
                alt="searchlogo"
                style={{ height: "24px", width: "24px" }}
              />
            </Box>

            <Input
              id="search"
              type="text"
              placeholder="Search or type a command"
              onChange={(e) => handleSearch(e.target.value)}
              fontSize="sm"
              border="none"
              outline="none"
              color="#757575"
              _focus={{
                border: "none",
                outline: "none",
                boxShadow: "none",
              }}
              bg={"transparent"}
              style={{
                outline: "none",
                boxShadow: "none",
              }}
            />
          </Box>
          <Spacer />
        </Flex>

        <Spacer />
        {loading ? (
          <Spin size="large" />
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={filteredData.map((item) => item.name)}
              strategy={verticalListSortingStrategy}
            >
              <Table
                components={{
                  body: {
                    row: SortableRow,
                  },
                }}
                rowKey="name"
                columns={columns}
                dataSource={filteredData}
                scroll={{ x: "max-content" }}
                size="middle"
                style={{
                  backgroundColor: "white",
                  borderRadius: 6,
                }}
                pagination={{
                  position: ["bottomRight"],
                  pageSize: 100,
                }}
                loading={busy && filteredData.length === 0}
              />
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
};

export default RestaurantsequenceTable;
